/**
 * Every tuning number for the pre-battle mini-game, in one place.
 *
 * These are the dials that decide how the board looks, how the seed moves, when
 * it stops, and how readily a colour mixture turns into a particular Garaimon.
 * They are gathered here rather than scattered through the code so they can be
 * adjusted together, and each one has a short note saying what it does and which
 * direction makes what difference.
 *
 * The values below are starting points chosen to be reasonable, not final
 * answers. They are expected to move once the launch sweep in
 * `scripts/collect_sweep.ts` has measured real boards.
 */

import type { RarityTier } from './types';

export type BoardLayoutFamily = 'bands' | 'islands' | 'centre-perimeter';

/**
 * The surface the seed rolls on: cloth, as a ball on a snooker table.
 *
 * The seed decelerates at a constant rate, so it comes to rest in finite time
 * instead of creeping, and it keeps its spin for long enough that the curve
 * continues well into the run rather than being spent in the first moment. At
 * the maximum spin it turns through roughly two and a half times the arc.
 *
 * A puck-on-air model was built alongside this while the two were being compared
 * on feel, and was removed once cloth was chosen. The seed is always on cloth.
 */

export interface CollectConfig {
  /** Canvas width in logical units. The battle arena uses the same 360, so both match on any phone height. */
  readonly logicalWidth: number;
  /** Height in logical units used when there is no container to measure, such as in tests. */
  readonly fallbackLogicalHeight: number;
  /**
   * Space reserved above the bottom of the board for the floating readout, as a
   * reference for a 360-CSS-pixel-wide screen.
   *
   * The readout is drawn in CSS pixels while the board is drawn in logical units,
   * so the same band is a different number of logical units on a narrow screen
   * than on a wide one. The mini-game measures its own readout and passes the
   * converted value, so the field reaches the band's top edge exactly at every
   * width. This value is the reference those measurements are checked against, and
   * the value the sweep and the tests use; it is not what a wide screen uses.
   *
   * The band holds one of three things, never two at once: the instruction while
   * the seed is being aimed, nothing while it runs, and the Battle button once it
   * has settled. It is a constant 80 CSS pixels tall plus a 4 pixel offset, sized
   * for the tallest state - the settled Battle button - so the button never covers
   * the lowest row of pixels.
   */
  readonly reservedBottom: number;
  /**
   * Space reserved at the top of the board for the floating interface, as a
   * reference for a 360-CSS-pixel-wide screen.
   *
   * The seed-fill meter and the target panel hang over the top of the field, and
   * both must stay readable, so the field starts below them rather than behind
   * them: every pixel of the board is then a pixel the player can see. Measured and
   * converted the same way as `reservedBottom`.
   *
   * A logical value, like `reservedBottom`, so the field is a fixed shape on every
   * screen. The panels are drawn in CSS pixels, so the gap below them is a little
   * larger on a wider board and a little smaller on a narrower one; the value here
   * clears them from 360 CSS pixels wide upwards.
   */
  readonly reservedTop: number;
  /** Distance between neighbouring pixels on the hidden grid. Smaller means denser. */
  readonly pixelSpacing: number;
  /** Random positional offset per pixel, as a fraction of the spacing. */
  readonly pixelJitterRatio: number;
  /** Idle floating amplitude, as a fraction of the spacing. */
  readonly floatAmplitudeRatio: number;
  /** Idle floating frequency range in radians per second. */
  readonly floatFrequencyRange: readonly [number, number];
  /** Layout families, one chosen per board. */
  readonly layoutFamilies: readonly BoardLayoutFamily[];
  /** How much of a region's influence bleeds into a neighbour, in region-size units. Higher means softer boundaries. */
  readonly regionSoftness: number;
  /** Amplitude of the smooth noise added to region scores, as a fraction of one region's strength. */
  readonly noiseAmplitudeRatio: number;
  /** Number of low-frequency noise waves summed for the smooth irregularity. */
  readonly noiseWaves: number;
  /**
   * Angular frequency of the first noise wave, in radians per logical unit, and
   * the step added for each further wave.
   *
   * These set the scale of the irregularity. A wavelength much longer than the
   * board makes the noise a single constant offset, which is no irregularity at
   * all; a wavelength of a few pixels turns the board into speckle. The values
   * here give lobes a handful of pixels across, which is what lets a minority
   * colour cluster in one place instead of scattering into confetti.
   */
  readonly noiseFrequencyBase: number;
  readonly noiseFrequencyStep: number;
  /** Number of regions the board uses per creature it must serve. */
  readonly regionsPerProfile: number;
  /**
   * The most regions one board may be split into, whatever its layout.
   *
   * Each region is one creature's stretch of board - the place its colours can
   * be gathered in its proportions - so a board that carries fewer regions than
   * creatures leaves some of them with nowhere to be aimed at. The sweep reports
   * those creatures as unreachable, because no launch comes close to them. This
   * is the bound that keeps the layout readable at the same time: past it the
   * regions are too small to gather a mixture from.
   */
  readonly maxRegionsPerBoard: number;
  /**
   * How many distinct colours one region's recipe may carry.
   *
   * There is no small fixed cap: a region whose recipe is meant to carry a
   * whole creature can list that many colours. Readability comes from a
   * dominant colour and the region's shape, not from limiting how many
   * colours a region is allowed to describe.
   */
  readonly regionRecipeColours: number;
  /**
   * The smallest share a region's leading colour is allowed to take.
   *
   * This exists only to keep a region readable as a shape when a profile is very
   * flat. It is a floor, not a target: a profile whose own leading colour already
   * carries more than this keeps its real proportion, because forcing every region
   * to a fixed dominant share would make the board far more one-coloured than the
   * creature it is meant to resemble - which is exactly how a faithful profile
   * becomes uncollectable.
   */
  readonly recipeLeadFloor: number;
  /**
   * Share of every pixel's expectation spent on the plan's floors rather than on
   * the region recipes. This is what lets a colour that only a minority region
   * asks for still reach the quantity the plan promised it.
   */
  readonly floorMixShare: number;
  /**
   * Share of the board the per-anchor floors may claim, alongside the dominant
   * structure. If the floors do not fit, they are scaled down proportionally and
   * the reduction is recorded as a recoverable allocation problem.
   */
  readonly availabilityBudget: number;
  /** Smallest share a demanded anchor is guaranteed on the board, before the budget is applied. */
  readonly anchorFloorShare: number;
  /** Neutrals always added to a board's colour pool, so lights and darks are available anywhere. */
  readonly neutralColours: readonly string[];

  /**
   * How far back from release the throw is read, in milliseconds.
   *
   * Long enough to contain the fling itself, so a player who straightens or
   * steadies their finger just before letting go still throws hard; short enough
   * that a movement made long ago cannot decide the power.
   */
  readonly throwWindowMs: number;
  /**
   * The span of movement one speed measurement covers, in milliseconds.
   *
   * This smooths out a single jittery pointer event while staying short enough to
   * still be "the throw". If the whole throw window is shorter than this, the
   * window is measured as one motion instead.
   */
  readonly speedSpanMs: number;
  /** A drag shorter than this many CSS pixels does nothing, so a tap stays a tap. */
  readonly tapThresholdPx: number;
  /** Upper bound on launch speed, in logical units per second. */
  readonly maxLaunchSpeed: number;
  /** Finger speed at which half of `maxLaunchSpeed` is reached. Lower means easier to launch hard. */
  readonly referenceFingerSpeed: number;
  /** Launch speed for a drag that is just above the tap threshold, so a weak swipe still moves. */
  readonly minimumLaunchSpeed: number;
  /**
   * How close a touch must land to the seed to grab it, in logical units.
   *
   * The seed is dragged like a physical object, so a touch that lands away from
   * it is not a throw at all. This is generous relative to the seed's drawn size
   * because a fingertip is much wider than the pixel it picks up.
   */
  readonly seedGrabRadius: number;
  /** Largest initial spin, in radians per second. Caps how sharply a curve can bend the path. */
  readonly maxSpin: number;
  /**
   * Ceiling on the drawn spin rate, in radians per second, for legibility.
   *
   * The spin is integrated over the flight, and at the top of the speed range the
   * seed crosses its own width more than once per frame, so the true rotation is
   * unreadable at exactly the moment a player wants to see it. This scales the
   * angle the seed is *drawn* at. The trajectory, the curve, the spin decay and
   * every measurement are untouched.
   *
   * A rotation faster than roughly a quarter turn per frame stops reading as a
   * spin and starts reading as a flicker, and above half a turn per frame it
   * appears to reverse. This is where the maximum spin is drawn, and lower spins
   * are drawn proportionally slower, so a hard curve spins fast and a gentle one
   * turns gently rather than every throw above a certain point looking the same.
   */
  readonly seedSpinVisualMaxRate: number;
  /**
   * How quickly the drawn spin fades while the seed is still in flight, per
   * second.
   *
   * Kept far below the physical spin decay on purpose. The seed should read as a
   * spinner that carries its turn through the whole run, not as one that gives up
   * its spin in the first moment; the spin decaying on screen at the same rate as
   * the physics would make a hard curve look almost stationary.
   */
  readonly seedSpinVisualFlightDampPerSecond: number;
  /**
   * How quickly the drawn spin winds down once the seed has come to rest, per
   * second.
   *
   * The seed's surface keeps turning after the seed itself has stopped, as a
   * spinner does, rather than freezing mid-rotation. This is the decay of that
   * last turn, and it is drawn only - the seed is already at rest as far as the
   * simulation is concerned.
   */
  readonly seedSpinDownPerSecond: number;
  /** How far back from release the curve is read, in milliseconds. */
  readonly curveWindowMs: number;
  /**
   * The curve is two signed measurements added together: how far the finger
   * wandered off its own throw line (swept area) and how sharply it turned
   * (turning angle). These weights decide how much each contributes.
   */
  readonly curveAreaWeight: number;
  readonly curveTurnWeight: number;
  /** Swept area per second that scores 1. Sets how wide an arc reads as a strong curve. */
  readonly curveAreaReference: number;
  /** Turning per second that scores 1. Sets how sharp a hook reads as a strong curve. */
  readonly curveTurnReference: number;
  /**
   * How much of each measurement is treated as no measurement at all.
   *
   * A hand cannot hold a line, so even a drag the player means as straight
   * wanders by a fraction of a pixel and turns by a fraction of a degree. Without
   * these floors that tremor is indistinguishable from a small deliberate curve
   * and is read as spin, which is why an attempted straight throw used to bend.
   * The floors are set above the measured tremor of a real straight drag, so a
   * drag inside them reads as exactly straight rather than as a small curve.
   */
  readonly curveAreaFloor: number;
  readonly curveTurnFloor: number;
  /** Throw speed below which the direction is taken from the drag as a whole instead. */
  readonly directionSpeedThreshold: number;

  /** Fixed simulation step in seconds, so results do not depend on frame rate. */
  readonly fixedStepSeconds: number;
  /**
   * Constant deceleration of the seed, in logical units per second squared.
   *
   * Cloth takes speed off a rolling ball at a roughly fixed rate, which is what
   * makes a ball stop rather than creep, and what makes a harder hit travel
   * disproportionately further: distance grows with the square of the launch
   * speed.
   */
  readonly tableFriction: number;
  /**
   * How quickly spin fades while flying.
   *
   * The spin lasts well into the run, so the curve is sustained rather than being
   * spent in the first moment.
   *
   * Kept below the point where the spin would outlive the seed's own travel. If
   * it faded more slowly than the speed, the radius of curvature, speed over
   * spin, would keep shrinking and a hard curve would coil the seed into a spiral
   * as it crawled. This value stays on the safe side of that line.
   */
  readonly clothSpinDecayPerSecond: number;
  /**
   * The arm, in logical units, that turns the seed's spin into a sliding speed at
   * a cushion contact.
   *
   * The spin in this game is a steering rate - radians per second of heading
   * change - and not the rolling rate of a real ball, so it has no radius of its
   * own. A cushion contact needs a sliding speed to act on, and this is the arm
   * that supplies it. It matters: with an arm that is too small the sliding speed
   * is far larger than the spin can justify, the friction impulse saturates, and
   * a single contact can manufacture spin the throw never had, or reverse it.
   */
  readonly cushionSpinArmPx: number;
  /**
   * How strongly a cushion grips the ball at the contact point. This is the
   * friction coefficient of cloth against cushion.
   *
   * The contact point of a spinning ball is moving sideways as the ball touches,
   * and cushion friction drags on that sideways motion. That drag is what pushes
   * the ball along the cushion as it rebounds - the running-side and check-side
   * effect, the one place where side spin really shows on a table. Higher means a
   * stronger kick off the cushion.
   */
  readonly cushionFriction: number;
  /**
   * The share of side spin that survives one cushion contact.
   *
   * A cushion is where side spin is spent. On a real table the contact consumes
   * most of it, and that is what stops a spinning ball being steered back into
   * the cushion it just hit, over and over. A contact only ever spends spin; it
   * never converts it back the other way, because a contact that reversed the
   * spin would steer the seed back into the cushion it had just left.
   */
  readonly cushionSpinRetention: number;
  /**
   * Ceiling on how far the seed's heading may turn in total, in radians.
   *
   * A safety net rather than a design element. The seed stops in finite time, so
   * the total turning is already bounded by the spin divided by its decay rate,
   * and the value here sits above what the sharpest throw reaches. It exists so
   * that no combination of dials can coil the seed in place for an unbounded
   * time, and it is deliberately not so tight that a legitimate hard curve ever
   * reaches it.
   */
  readonly maxTurnRadians: number;
  /** Speed lost at a wall: the normal part is multiplied by this. Below 1 loses speed, exactly 1 conserves it. */
  readonly wallRestitution: number;
  /** Below this speed the seed is treated as stopped. */
  readonly stopSpeed: number;
  /** Radius with which the seed gathers pixels it directly touches. */
  readonly contactRadius: number;
  /** Speed below which absorption starts to charge. */
  readonly attractionOffSpeed: number;
  /**
   * How sharply the charging concentrates towards the end of the slow-down.
   *
   * The rate the well charges at is an exponential function of how slow the seed
   * is, and this is the exponent. At 1 the ramp is linear. Higher values make the
   * early part of the slow-down charge almost nothing and put most of the charge
   * into the last of it, which is the shape of a spinner dying: it drinks the
   * field as it runs down rather than hauling a vacuum from the moment it is
   * thrown. It must not be so high that the charge is invisible before the seed
   * stops, or the well appears to switch on at the stop rather than swell.
   */
  readonly attractionExponent: number;
  /**
   * How fast the absorption well charges, per second, at full ease.
   *
   * The charge is what the reach and the pull are both read from, and it only
   * ever grows, so this is the single number that decides how long the swell
   * lasts. It charges at a fraction of this rate while the seed is still moving
   * fast, at this full rate once the seed has stopped, and keeps charging after
   * the stop - which is what makes the well visibly grow instead of being
   * complete the moment the seed halts.
   */
  readonly attractionChargePerSecond: number;
  /** The reach at full charge, which is where the swell finishes. */
  readonly maxAttractionRadius: number;
  /** Strongest pull applied to a pixel on the seed's own position, at full charge. */
  readonly maxAttractionStrength: number;
  /**
   * The share of the pull that survives at the very edge of the reach.
   *
   * The pull falls off with distance, strongest at the seed and weakest at the
   * rim. It cannot fall to nothing, or a pixel that settles on the rim would sit
   * there being pulled by zero forever; this is the floor that still brings it
   * in. Lower makes the gravity well steeper.
   */
  readonly pullFloor: number;

  /** How many pixels must be gathered for the attempt to count as complete. */
  readonly minimumCollected: number;
  /**
   * Seconds over which the last-resort stream flies the nearest remaining pixels
   * into the seed.
   *
   * Reached only at the attempt ceiling, never as an ordinary part of a settling
   * seed: it exists so that the promise that every attempt ends cannot be broken
   * by a board whose colours sit further away than any absorption radius reaches.
   * It moves the real pixels, so their real colours are what gets counted.
   */
  readonly finalStreamSeconds: number;
  /** Hard ceiling on the whole attempt after launch, in seconds. */
  readonly maxAttemptSeconds: number;

  /** Falloff width of the match score. Smaller makes the score stricter about colour ratios. */
  readonly matchTolerance: number;
  /**
   * How strongly a better match is favoured: the exponent the match score is
   * raised to before the rarity weight is applied.
   *
   * There is deliberately no additive floor. A creature whose colours were not
   * gathered scores zero on the match and therefore has no chance at all, which
   * is what makes the chance something the player earns rather than something
   * granted regardless. Higher concentrates the chance on the best match; 1.5
   * lets a well-aimed throw clearly lead without making the outcome a foregone
   * conclusion.
   */
  readonly sharpness: number;
  /**
   * Share of a board's pixels that are any-colour pixels rather than a colour.
   *
   * "Any-colour" is the narrative replacement for the old exploration floor: a
   * pixel that is every colour at once, so gathering it keeps every Garaimon
   * possible. The seed the player launches always counts as one of these, so a
   * reveal can never be reached with none. They are kept sparse on the field so
   * that reaching for a rare Garaimon takes deliberately aiming at them rather
   * than being handed to the player by the board.
   */
  readonly wildcardShare: number;
  /** How a tie between near-identical profiles is settled. Rarity decides the weight, never the tie. */
  readonly tieBreak: 'speciesIdAscending';
}

export const COLLECT_CONFIG: CollectConfig = {
  logicalWidth: 360,
  fallbackLogicalHeight: 600,
  reservedBottom: 84,
  reservedTop: 36,
  pixelSpacing: 10,
  pixelJitterRatio: 0.1,
  floatAmplitudeRatio: 0.08,
  floatFrequencyRange: [0.7, 1.9],
  layoutFamilies: ['bands', 'islands', 'centre-perimeter'],
  regionSoftness: 0.5,
  noiseAmplitudeRatio: 0.32,
  noiseWaves: 3,
  noiseFrequencyBase: 0.05,
  noiseFrequencyStep: 0.035,
  regionRecipeColours: 8,
  regionsPerProfile: 1,
  maxRegionsPerBoard: 16,
  recipeLeadFloor: 0.3,
  floorMixShare: 0.15,
  availabilityBudget: 0.6,
  anchorFloorShare: 0.02,
  neutralColours: ['#ebe1c0', '#555754'],
  throwWindowMs: 300,
  speedSpanMs: 70,
  tapThresholdPx: 24,
  maxLaunchSpeed: 2400,
  referenceFingerSpeed: 650,
  minimumLaunchSpeed: 220,
  seedGrabRadius: 32,
  maxSpin: 20,
  seedSpinVisualMaxRate: 55,
  seedSpinVisualFlightDampPerSecond: 0.35,
  seedSpinDownPerSecond: 1.4,
  curveWindowMs: 260,
  curveAreaWeight: 0.6,
  curveTurnWeight: 0.4,
  curveAreaReference: 26000,
  curveTurnReference: 7,
  curveAreaFloor: 500,
  curveTurnFloor: 0.9,
  directionSpeedThreshold: 40,
  fixedStepSeconds: 1 / 120,
  tableFriction: 320,
  clothSpinDecayPerSecond: 2.2,
  cushionSpinArmPx: 14,
  cushionFriction: 0.12,
  cushionSpinRetention: 0.3,
  maxTurnRadians: 4.5 * Math.PI,
  wallRestitution: 0.85,
  stopSpeed: 26,
  contactRadius: 7,
  attractionOffSpeed: 340,
  attractionExponent: 2.2,
  attractionChargePerSecond: 0.55,
  maxAttractionRadius: 150,
  maxAttractionStrength: 300,
  pullFloor: 0.2,
  minimumCollected: 150,
  finalStreamSeconds: 0.8,
  maxAttemptSeconds: 8,
  matchTolerance: 0.13,
  sharpness: 1.5,
  wildcardShare: 0.015,
  tieBreak: 'speciesIdAscending',
};

/**
 * One rung of the rarity ladder, and everything the encounter reads off it.
 *
 * The ladder is stated once, in order, so the encounter's weights, its ceilings
 * and the reel's colours cannot drift apart. It is drawn from the convention the
 * loot games that established the idea use (grey, green, blue, purple, gold),
 * with the blue taken to a light cyan so the rare rung reads as a gem rather than
 * as a flat UI blue.
 */
export interface RarityRung {
  readonly tier: RarityTier;
  /** Shown beside the reel, so the tier is never carried by colour alone. */
  readonly label: string;
  /**
   * How strongly this tier competes for the encounter before the colour match is
   * applied. The weights are the world's natural rarity: they decide the odds
   * when the player has gathered nothing to prefer.
   */
  readonly weight: number;
  /**
   * The most of the encounter this tier may ever take.
   *
   * This is what makes a stated figure like "a legendary is at most 0.1%" true
   * by construction rather than by hope: no amount of good aiming, and no number
   * of any-colour pixels, can push a tier past its ceiling. Uncommon and common
   * are uncapped, because a board that is mostly ordinary creatures should still
   * be able to produce one.
   */
  readonly ceiling: number;
  /** Outline colour for the rarity reel, and the colour of the roster's rarity label. */
  readonly colour: string;
}

/** The ladder, common first. The order is the order the reels settle in. */
export const RARITY_LADDER: readonly RarityRung[] = [
  { tier: 'common', label: 'Common', weight: 1, ceiling: Number.POSITIVE_INFINITY, colour: '#b9bdb6' },
  { tier: 'uncommon', label: 'Uncommon', weight: 0.55, ceiling: Number.POSITIVE_INFINITY, colour: '#5fd97a' },
  { tier: 'rare', label: 'Rare', weight: 0.25, ceiling: 0.25, colour: '#7fd8e8' },
  { tier: 'super-rare', label: 'Super Rare', weight: 0.05, ceiling: 0.03, colour: '#b47cf0' },
  { tier: 'legendary', label: 'Legendary', weight: 0.01, ceiling: 0.001, colour: '#f0c94a' },
];

const RUNG_BY_TIER = new Map(RARITY_LADDER.map((rung) => [rung.tier, rung] as const));

/**
 * A tier's rung. A missing tier is a content or config bug rather than a state to
 * carry on through, so it is reported instead of being substituted.
 */
export function rarityRung(tier: RarityTier): RarityRung {
  const rung = RUNG_BY_TIER.get(tier);
  if (!rung) {
    throw new Error(`Garaimon: "${tier}" is not a rung on the rarity ladder, so its encounter weight and ceiling are undefined.`);
  }
  return rung;
}


/**
 * A rough estimate of how many pixels a straight pass gathers, used only to
 * sanity-check the minimum above against the board. It ignores repeated coverage
 * and the walls, so it is a starting value, not a guarantee.
 */
export function estimatePathCollection(pathLength: number, pixelDensity: number): number {
  return 2 * COLLECT_CONFIG.contactRadius * pathLength * pixelDensity;
}
